import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { initials } from './Avatar';

const Panel = styled.div`
  margin: -6px 0 16px 36px;
  max-width: 78%;
  box-sizing: border-box;
  border: 1px solid #d9e0e8;
  border-radius: 14px;
  background: white;
  overflow: hidden;

  @media (max-width: 480px) {
    max-width: 85%;
  }
`;

const SearchRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  color: #64748b;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: #1e293b;

  &::placeholder {
    color: #94a3b8;
  }
`;

// Scrolls inside the chat so a long contact list never pushes the conversation away
const List = styled.div`
  max-height: 196px;
  overflow-y: auto;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #eef1f5;
  background: white;
  text-align: left;
  font: inherit;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &:focus-visible {
    background: #f1f5f9;
    outline: none;
  }
`;

const Initials = styled.span`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e7edf3;
  color: #275386;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Name = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1b3c66;
`;

const Detail = styled.span`
  display: block;
  font-size: 12px;
  color: #64748b;
`;

const Note = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// The client's saved contacts, so the user picks their own name instead of typing name and number
const ContactPicker = ({ sessionId, onPick, disabled }) => {
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState(null);  // null while the first search runs

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { REACT_APP_BACKEND_BOT_URL, REACT_APP_AUTH_TOKEN } = process.env;
        const response = await fetch(
          `${REACT_APP_BACKEND_BOT_URL}/chat/contacts/${sessionId}?q=${encodeURIComponent(query.trim())}`,
          { headers: { Authorization: `${REACT_APP_AUTH_TOKEN}` } }
        );
        const data = response.ok ? await response.json() : { contacts: [] };
        if (!cancelled) setContacts(data.contacts || []);
      } catch (e) {
        if (!cancelled) setContacts([]);
      }
    }, query ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, query]);

  // Nothing saved for this client at all: the typed fallback is all there is
  if (contacts && contacts.length === 0 && !query) return null;

  return (
    <Panel role="group" aria-label="Pick your name">
      <SearchRow>
        <SearchIcon />
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your name"
          aria-label="Search your name"
          disabled={disabled}
        />
      </SearchRow>
      <List>
        {contacts === null && <Note>Loading contacts…</Note>}
        {contacts && contacts.length === 0 && <Note>No one by that name.</Note>}
        {contacts && contacts.map((contact) => (
          <Row key={contact.id} type="button" disabled={disabled} onClick={() => onPick(contact)}>
            <Initials aria-hidden="true">{initials(contact.name)}</Initials>
            <span>
              <Name>{contact.name}</Name>
              <Detail>{[contact.role, contact.phone_masked].filter(Boolean).join(' · ')}</Detail>
            </span>
          </Row>
        ))}
      </List>
      <Note>Not in the list? Type your name and number below.</Note>
    </Panel>
  );
};

export default ContactPicker;
